import { create } from 'zustand';
import { normalizeError } from '@/errors/normalize';

type NormalizedError = ReturnType<typeof normalizeError>;
type ErrorHandler = (error: NormalizedError) => void;

interface ErrorEntry {
  error: NormalizedError;
  timestamp: number;
}

export interface ErrorBusState {
  // Internal state
  subscribers: Set<ErrorHandler>;
  recentErrors: ErrorEntry[];

  // Publish an error to all subscribers
  publish: (errorLike: unknown) => void;
  // Subscribe to error events; returns an unsubscribe function
  subscribe: (handler: ErrorHandler) => () => void;
}

// Store for error bus pub/sub pattern
export const useErrorBusStore = create<ErrorBusState>()((set, get) => ({
  subscribers: new Set<ErrorHandler>(),
  recentErrors: [],

  publish: errorLike => {
    const normalized = normalizeError(errorLike);
    const now = Date.now();
    const state = get();

    // Filter out errors older than 5 seconds
    const recentErrors = state.recentErrors.filter(
      entry => now - entry.timestamp < 5000,
    );

    // Check for duplicates
    const duplicate = recentErrors.find(
      entry =>
        entry.error.code === normalized.code &&
        entry.error.message === normalized.message,
    );

    if (!duplicate) {
      // Add to recent errors
      set({
        recentErrors: [...recentErrors, { error: normalized, timestamp: now }],
      });

      // Notify all subscribers
      state.subscribers.forEach(handler => handler(normalized));
    }
  },

  subscribe: handler => {
    const state = get();
    const newSubscribers = new Set(state.subscribers);
    newSubscribers.add(handler);
    set({ subscribers: newSubscribers });

    // Return unsubscribe function
    return () => {
      const currentState = get();
      const updatedSubscribers = new Set(currentState.subscribers);
      updatedSubscribers.delete(handler);
      set({ subscribers: updatedSubscribers });
    };
  },
}));

// Convenience hooks for common use cases
export const useErrorPublish = () => useErrorBusStore(state => state.publish);
export const useErrorSubscribe = () =>
  useErrorBusStore(state => state.subscribe);

// Backwards-compatible hook
export const useErrorBus = () => {
  const publish = useErrorBusStore(state => state.publish);
  const subscribe = useErrorBusStore(state => state.subscribe);
  return { publish, subscribe };
};
